import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { decode } from "src/utils/flexiblePolyline";

import {
  getBoundingBox,
  getRandomValueToForceRerender,
  ICoordinates,
  IRoutePoint,
  ISandboxMarker,
} from "src/components/Map";

import { RioMap } from "@components";
import Notification from "@rio-cloud/rio-uikit/Notification";
import UpdateSidebarRoute from "src/components/UpdateSidebarRoute";
import { createAndSendRoute, fetchDetail, updateRouteForTrip, updateRoute } from "src/services/routes";
import {
  defineAccountId,
  defineAreasRework,
  defineAvoidAreas,
  defineAvoidToll,
  defineDestination,
  defineDistance,
  defineDurationWithStops,
  defineOrigin,
  defineRouteCost,
  defineRouteDeviation,
  defineRouteName,
  defineRoutes,
  defineStops,
  defineVehicleInfo,
  defineRouteId,
  defineCopyAreasRework,
} from "src/store/routes/RoutesSlice";
import { IResponseFetchDetail, ISendRouteParams, Route } from "src/types/routes";

import { ApplicationLayout } from "@rio-cloud/rio-uikit";
import { clearTrip, RootState, setBackupTrip, setIsCustomTrip, setRouteTripSelected, setTripUpdateClean, setTripUpdateDetailData, setTripUpdateRouteCostData, useAppSelector } from "@store";
import { CreateAreaReworkPayload, NavigateState } from "@types";
import { buildLinkedVehicleItem, convertMetersToKm, formatCategory, formatDateInUtc, formatMetersMaskInNumber, formatPolygon, getDnGroupIdFromUrl, getDnGroupNameFromUrl, safeFormatDateEn } from "@utils";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { calculateOffsetCoordinates } from "src/components/Dynamics/DynamicContentArea";
import { ProgressOptions } from "src/components/Progress/Trip";
import { convertSecondsToHours, convertTimeStringToSeconds } from "src/components/RegisterRoute";
import { MapDrawingProvider } from "src/providers/MapDrawingContext";
import { getAllAreas } from "src/services/areas";
import { sendTripCustom, sendTripUpdateCopy } from "src/services/trips";
import { ReworkAreasResponse } from "src/types/areas";
import { createRouteCost, getByIdRouteCost } from "../../services/costSimulator";
import { CreateRouteCostPayload } from "../../types/costSimulator";
import useFromFileData from "./components/useFromFileData";

const RoutesTripUpdate = () => {
  const dispatch = useDispatch();
  const profile = useAppSelector((state) => state.login.userProfile);
  const stateTrips = useAppSelector((state) => state.trips);
  const [position, setPosition] = useState<ICoordinates | undefined>({
    lat: -23.5475,
    lng: -46.63611,
  });

  const [boundingBox, setBoundingBox] = useState<
    { top: number; left: number; right: number; bottom: number } | undefined
  >();

  const [segments, setSegments] = useState<IRoutePoint[]>([]);
  const [markers, setMarkers] = useState<ISandboxMarker[]>([]);
  const [search] = useSearchParams();

  const durationWithStops = useSelector((state: RootState) => state.routes.durationWithStops);

  const [isCustom, setIsCustom] = useState(false);

  const location = useLocation();
  const navigateState = location.state as NavigateState | null;

  useFromFileData();

  const {
    driversSelected,
    nameVehicleSelected,
    idVehicleSelected,
    scheduleDateSelected,
    scheduleHoursSelected,
    idTripSelected,
  } = useAppSelector((state) => state.trips);

  const navigate = useNavigate();
  const params = new URLSearchParams(search);

  const [content, setContent] = useState(3);

  const getRouteIdAndTokenFromURL = () => {
    const params = new URLSearchParams(search);
    const routeId = params.get("id");
    const backPage = params.get("backPage");
    // Keep routeId on backPage so edit→smart-routes→voltar does not lose body.id
    // and fall into create×3 instead of /copy.
    const csvData = location.state?.csvData;
    if (backPage) return { routeId, csvData: null, backPage };

    return { routeId, csvData };
  };

   const { routeId } = getRouteIdAndTokenFromURL();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const backPage = params.get("backPage");
    if (backPage) return;
    if (stateTrips?.fetchDetailData) return;

    if (!routeIdFromStore) {
      dispatch(defineOrigin(null));
      dispatch(defineDestination(null));
      dispatch(defineRoutes([]));
      dispatch(defineStops([]));
      dispatch(defineRouteCost({} as CreateRouteCostPayload));
      dispatch(defineRouteName(""));
      dispatch(defineAccountId(""));
      dispatch(defineAvoidToll(false));
      dispatch(defineRouteId(null));
    }

    return () => {
      dispatch(defineRouteDeviation(0));
      if (!skipClearOnUnmount.current) {
        dispatch(defineStops([]));
        dispatch(defineOrigin(null));
        dispatch(defineDestination(null));
      }
    };
  }, []);

  useEffect(() => {
    if (routeId) {
      fetchRouteDetails();
    }
  }, [routeId]);


  const fetchRouteDetails = async () => {
    try {
      if (stateTrips?.fetchDetailData) {
        skip.current = true;
        populateMap(stateTrips.fetchDetailData, stateTrips.routeCostData);
        return;
      }

      const { routeId, csvData, backPage } = getRouteIdAndTokenFromURL();
      if (backPage) return;
      if (routeId) {
        skip.current = true;
        const response = await fetchDetail(routeId);
        let responseCost;

        try {
          responseCost = await getByIdRouteCost(routeId);
        } catch (error) {
          console.log("Não foi possível carregar os detalhes do custo da rota.");
        }

        dispatch(setTripUpdateDetailData(response));
        dispatch(setBackupTrip(response.data?.result));
        dispatch(setTripUpdateRouteCostData(responseCost));
        populateMap(response, responseCost);
      } else {
        console.log("csvData >>>>>", csvData);
      }
    } catch (error) {
      console.log("error >>>>>>>> ", error);
      console.error(error);
      Notification.error("Não foi possível carregar os detalhes da rota.");
    }
  };

  const skip = useRef(false);
  // Preserva a rota no Redux durante a ida temporária ao smart-routes (otimização).
  // O cleanup de unmount continua limpando em todas as outras saídas (troca de
  // viagem/rota), mantendo o comportamento do fix de checkpoints (8fb640f).
  const skipClearOnUnmount = useRef(false);

  const handleRoutes = (routes: Route[], via: any[]) => {
    if (routes && routes.length) {
      let ArrayOfShape: any[] = [];
      const route = routes?.[0];
      // Garante que sections seja sempre array
      const data = route?.sections ?? [];

      const getPolylineArray = data.map((item) => {
        return {
          shape: decode(item.polyline),
        };
      });

      const markers = (data ?? []).reduce((acum: ISandboxMarker[], curr, index, arr) => {
        if (index === 0) {
          const markerOrigin: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["start"],
              name: "",
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.departure.place.location.lat,
              lng: curr.departure.place.location.lng,
            },
          };
          acum.push(markerOrigin);
        }
        if (index === arr.length - 1) {
          const markerDestination: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["finish"],
              name: "",
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.arrival.place.location.lat,
              lng: curr.arrival.place.location.lng,
            },
          };
          acum.push(markerDestination);
        } else {
          const markerStopover: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: [curr.transport.mode === "ferry" ? "rioglyph rioglyph-ship" : "rioglyph rioglyph-pushpin"],
              name: via?.[index]?.position?.label || "",
              markerColor: curr.transport.mode === "ferry" ? "bg-map-marker-asset" : "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.arrival.place.location.lat,
              lng: curr.arrival.place.location.lng,
            },
          };
          acum.push(markerStopover);
        }

        return acum;
      }, []);

      getPolylineArray.forEach((item) => {
        ArrayOfShape.push(item.shape.polyline);
      });
      const concatArrays = ArrayOfShape.flat();
      const getPoints = concatArrays.map((value) => {
        return {
          lat: value[0].toString(),
          lng: value[1].toString(),
        };
      });
      setSegments(getPoints as IRoutePoint[]);
      setMarkers(markers);

      if (getPoints?.length > 0) {
        setPosition(undefined);
        setBoundingBox(getBoundingBox(getPoints));
      } else if (markers?.length > 0) {
        setPosition(markers[0].position);
      }

      const safeVia = Array.isArray(via) ? via : [];
      const safeData = Array.isArray(data) ? data : [];
      const totalRouteDurationSeconds = safeData.reduce((acc: number, curr) => {
        return acc + (curr.summary?.duration || 0);
      }, 0);

      const totalStopsSeconds = safeVia.reduce((acc: number, stop: any) => {
        return acc + convertTimeStringToSeconds(stop?.stayTime || "00:00");
      }, 0);

      const totalDurationSeconds = totalRouteDurationSeconds + totalStopsSeconds;
      const finalDuration = convertSecondsToHours(totalDurationSeconds);
      dispatch(defineDurationWithStops(finalDuration));

      const { distance } = routes[0]?.sections?.reduce(
        (acum, curr) => {
          acum.distance += curr.summary.length;
          return acum;
        },
        { distance: 0 }
      );

      dispatch(defineDistance(convertMetersToKm(distance)));
    } else {
      setMarkers([]);
      setSegments([]);
    }
  };

  const parseStayTime = (timeString: string): string => {
    const baseDate = new Date("2000-01-01T00:00:00");
    const targetDate = new Date(timeString);

    const timeDifferenceMs = targetDate.getTime() - baseDate.getTime();
    const hours = Math.floor(timeDifferenceMs / 3600000);
    const minutes = Math.floor((timeDifferenceMs % 3600000) / 60000);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const populateMap = ({ data }: IResponseFetchDetail, costResponse?: CreateRouteCostPayload) => {
    dispatch(
      defineOrigin({
        id: data.result.originRoute.id,
        label: data.result.originRoute.address.label,
        lng: data?.result?.originRoute?.long,
        lat: data.result.originRoute.lat,
        originChecklistId: data.result.originRoute?.checklist?.id,
        originChecklistQuantity: data.result.originRoute?.checklist?.quantity ?? 0,
      })
    );
    dispatch(
      defineDestination({
        id: data.result.destinyRoute.id,
        label: data.result.destinyRoute.address.label,
        lng: data.result.destinyRoute.long,
        lat: data.result.destinyRoute.lat,
        destinyChecklistId: data.result.destinyRoute?.checklist?.id,
        destinyChecklistQuantity: data.result.destinyRoute?.checklist?.quantity ?? 0,
      })
    );

    dispatch(defineRoutes(data.result.responseHere.routes));
    dispatch(defineAvoidToll(data.result.roadParameters?.avoidToll ?? false));
    const stops = data.result.stops.map((stop) => {
      return {
        id: stop.id,
        category: stop.stop.category,
        order: stop.stopQueueOrder,
        stayTime: parseStayTime(stop.stayTime as string),
        position: {
          lat: stop.stop.position.lat,
          lng: stop.stop.position.long,
          label: stop.stop.position.address.label,
        },
        checkpointId: stop.checkpointId?.id ?? null,
      };
    });

    dispatch(defineRouteDeviation(data.result.rangeToleranceLimit ?? 0));
    dispatch(defineStops(stops));
    dispatch(defineRouteName(data.result.routeName));
    dispatch(defineRouteCost(costResponse ?? ({} as CreateRouteCostPayload)));
    dispatch(defineAccountId(data.result.accountId));
    
    dispatch(
      defineVehicleInfo({
        grossWeight: (data.result.vehicleVocationalInfo.maxWeight / 1000).toFixed(2)?.toString().replace(/\./g, ","),
        height: (data.result.vehicleVocationalInfo?.height / 100)?.toString().replace(/\./g, ","),
        lenght: (data.result.vehicleVocationalInfo?.comTotal / 100)?.toString().replace(/\./g, ","),
        width: (data.result.vehicleVocationalInfo?.width / 100)?.toString().replace(/\./g, ","),
        type: data.result.vehicleVocationalInfo.vehicleType,
        maxAxleLoad: data.result.vehicleVocationalInfo.maxWeightAxle?.toString().replace(/\./g, ","),
        numberOfAxles: data.result.vehicleVocationalInfo.numberAxle?.toString().replace(/\./g, ","),
        shippedHazardousGoods: data.result.vehicleVocationalInfo.dangerClassification,
        trailerAxles: data.result.vehicleVocationalInfo.trailerAxle?.toString().replace(/\./g, ","),
        pollutantClass: data.result.vehicleVocationalInfo.pollutantClass?.toString(),
        maxLoadWeight: data.result.vehicleVocationalInfo.maxLoadWeight ?? 0,
        weightType: data.result.vehicleVocationalInfo.weightType,
      })
    );
    handleRoutes(data.result.responseHere.routes, stops);
  };

  const onContent = (idx?: number) => {
    const index = idx ?? 0
    setContent(index);
    navigate(ProgressOptions[index]?.route + '?' + params.toString(), { state: { edit: navigateState?.edit, cvs: navigateState?.csv } })
  }

  const submitTripUpdate = async (body: any) => {
    try {
      const { smartRoutePreference, ...rest } = body;

      if (smartRoutePreference?.energy || smartRoutePreference?.fastest || smartRoutePreference?.economic) {
        const routeData = {
          origin: body.originRoute,
          destination: body.destinyRoute,
          vehicle: body.vehicleVocationalInfo,
          stops: body.stops || body.via,
          routeName: body.routeName,
          accountId: body.accountId,
          responseHere: body.responseHere,
          tripId: idTripSelected,
          areas: body.areas || [],
          avoidToll: body.roadParameters?.avoidToll ?? false,
          distance: body.distance,
          duration: body.duration,
          routeCost: body.costs || {
            route: "",
            costTypes: [],
            valuePerUnit: {},
            totalCost: 0,
            tollCost: 0,
            currencyType: 0,
            truckType: 0,
          },
        };
        skipClearOnUnmount.current = true;
        navigate("/routing/trips/smart-routes", {
          state: {
            smartRoutePreference,
            routeData,
            body,
            edit: navigateState?.edit,
            csv: navigateState?.csv,
            selectedFuelOption: body?.fuelOption,
            navigateTo: {
              route: "/routing/trips/custom?" + params.toString() + "&trip=true&backPage=true",
              params: {
                state: {
                  smartRoutePreference,
                  routeData,
                  body,
                  edit: navigateState?.edit,
                },
              },
            },
          },
        });
      } else {
        await sendTripUpdateCopy(rest, idTripSelected as string);
        Notification.success("Viagem editada com sucesso.");
        dispatch(clearTrip());
        dispatch(defineRouteId(null));
        navigate("/");
      }
    } catch (error: any) {
      
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message || "Motorista com viagem neste período.");
      } else {
        Notification.error("Não foi possível enviar a viagem.");
      }
    }
  };

  const submitTripSmartRoute = async (body: any) => {
    try {
      const { smartRoutePreference, ...rest } = body;
      const params = new URLSearchParams(search);
      params.set("custom", isCustom?.toString());
      if (smartRoutePreference?.energy || smartRoutePreference?.fastest || smartRoutePreference?.economic) {
        const routeData = {
          origin: body.originRoute,
          destination: body.destinyRoute,
          vehicle: body.vehicleVocationalInfo,
          stops: body.stops || body.via,
          responseHere: body.responseHere,
          routeName: body.routeName,
          accountId: body.accountId,
          tripId: idTripSelected,
          areas: body.areas || [],
          avoidToll: body.roadParameters?.avoidToll ?? false,
          distance: body.distance,
          duration: body.duration,
          routeCost: body.costs || {
            route: "",
            costTypes: [],
            valuePerUnit: {},
            totalCost: 0,
            tollCost: 0,
            currencyType: 0,
            truckType: 0,
          },
        };
        skipClearOnUnmount.current = true;
        navigate("/routing/trips/smart-routes", {
          state: {
            smartRoutePreference,
            routeData,
            body,
            edit: navigateState?.edit,
            csv: navigateState?.csv,
            selectedFuelOption: body?.fuelOption,
            navigateTo: {
              route: "/routing/trips/custom?" + params.toString() + "&trip=true&backPage=true",
              params: {
                state: {
                  smartRoutePreference,
                  routeData,
                  body,
                  edit: navigateState?.edit,
                },
              },
            },
          },
        });
      }
      else {
        if (navigateState?.csv) {
          createWithCSV(body)
        } else {
          create(body);
        }
      }
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message || "Motorista com viagem neste período.");
      } else {
        Notification.error("Não foi possível enviar a viagem.");
      }
    }
  };

  const { routeCost: routeCostStore, routeId: routeIdFromStore } = useAppSelector((state) => state.routes);

  const create = async (body: any, routeId?: string) => {
    const idRoute = routeId ? routeId : body.id;

    try {
      let response: any;

      if (routeIdFromStore && !routeId) {
        response = await updateRoute({
          ...body,
          id: routeIdFromStore,
          routeName: body?.routeName + ' - PERSONALIZADO'
        });
      } else {
        response = await updateRouteForTrip({
          ...body,
          routeName: body?.routeName + ' - PERSONALIZADO'
        });
      }

      if (response?.status === "success" && response.data.result && !routeIdFromStore) {
        dispatch(defineRouteId(response.data.result));
      }

      if (response?.status === "success" && routeCostStore?.totalCost > 0) {
        const updatedRouteCost = {
          ...routeCostStore,
          route: response.data.result,
          routeId: response.data.result
        };
        await createRouteCost(updatedRouteCost);
      }

      const linkedVehicleItem = buildLinkedVehicleItem(
        {
          assetId: idVehicleSelected as string,
          assetName: nameVehicleSelected as string,
          routeId: response?.data.result as string,
          startDateTime: `${scheduleDateSelected}T${scheduleHoursSelected}:00+00:00`,
        },
        driversSelected
      );

      const bodyTrip = {
        linkedVehicles: [linkedVehicleItem],
      };

      await sendTripCustom(bodyTrip, idRoute as string);
      Notification.success("Viagem enviada com sucesso.");

      setTimeout(() => {
        dispatch(clearTrip());
        dispatch(defineRouteId(null));
        navigate("/routing/trips");
      }, 1000);
    } catch (error: any) {
      const err = JSON.parse(JSON.stringify(error));
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message || "Motorista com viagem neste período.");
      } else {
        Notification.error("Não foi possível enviar a viagem.");
      }
    }
  }

  const createWithCSV = async (body: any) => {
    try {
      const responseCreate = await createAndSendRoute(body);
      await create(body, responseCreate?.data?.result);
    } catch (error: any) {
      const err = JSON.parse(JSON.stringify(error));
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message || "Motorista com viagem neste período.");
      } else {
        Notification.error("Não foi possível enviar a viagem.");
      }
    }
  }

  const sendRoute = async ({
    origin,
    destination,
    routes,
    stops,
    value,
    accountId,
    routeDeviation,
    avoidToll,
    vehicle,
    smartRoutePreference,
    routeCost,
    distance,
    selectedFuelOption,
  }: ISendRouteParams) => {
    const { routeId: routeIdFromURL } = getRouteIdAndTokenFromURL();
    const params = new URLSearchParams(search);

    const routeId = routeIdFromStore || routeIdFromURL;

    const fuelOption = parseInt(selectedFuelOption as string);

    const linkedVehicleItem = buildLinkedVehicleItem(
      {
        assetId: idVehicleSelected as string,
        assetName: nameVehicleSelected as string,
        routeId: (routeIdFromStore || routeIdFromURL) as string,
        startDateTime: `${scheduleDateSelected}T${scheduleHoursSelected}:00+00:00`,
        startDateTimeUTC: formatDateInUtc(`${scheduleDateSelected}T${scheduleHoursSelected}`),
      },
      driversSelected
    );

    const body = {
      id: routeId,
      accountId,
      routeName: value,
      originRoute: {
        id: origin.id,
        address: {
          label: origin.label,
        },
        addressStop: {
          label: origin.label,
        },
        lat: origin.lat,
        lng: origin.lng,
      },
      destinyRoute: {
        id: destination.id,
        address: {
          label: destination.label,
        },
        addressStop: {
          label: destination.label,
        },
        lat: destination.lat,
        lng: destination.lng,
      },
      originChecklistId: origin.originChecklistId,
      destinyChecklistId: destination.destinyChecklistId,
      rangeToleranceLimit: routeDeviation ?? 0,
      stops: stops.map((stop, index) => ({
        id: stop.id,
        name: `Parada ${index + 1}`,
        category: stop.category,
        rangeLimitMeters: "0",
        position: {
          lat: stop.position?.lat,
          lng: stop.position?.lng,
          addressStop: {
            label: stop?.position?.label || "",
          },
        },
        stayTime: `${stop.stayTime}:00`,
        note: formatCategory(stop.category),
        type: "STOPOVER",
        order: stop.order,
        checkpointId: stop.checkpointId ?? null,
      })),
      roadParameters: {
        trafficConditions: "false",
        avoidToll,
        avoidRoad: false,
        ignoreTrafficRestrictions: false,
      },
      vehicleVocationalInfo: {
        height: formatMetersMaskInNumber(vehicle?.height) || 0,
        width: formatMetersMaskInNumber(vehicle?.width) || 0,
        comTotal: formatMetersMaskInNumber(vehicle?.lenght) || 0,
        grossWeight: formatMetersMaskInNumber(vehicle?.grossWeight, "T") || 0,
        maxAxleLoad: parseFloat(vehicle.maxAxleLoad),
        numberOfAxles: parseInt(vehicle.numberOfAxles),
        trailerAxles: parseInt(vehicle.trailerAxles),
        // vehicleType: vehicle.type,
        type: vehicle?.type,
        shippedHazardousGoods: vehicle?.shippedHazardousGoods,
        pollutantClass: vehicle?.pollutantClass,
      },
      costs: {
        tollValue: routeCost.tollCost || 0,
        operativeCosts: routeCost.valuePerUnit?.[9]?.[0] || 0,
        fuelAverageCosts: routeCost.valuePerUnit?.[fuelOption]?.[1] || 0,
        averageConsume: routeCost.valuePerUnit?.[fuelOption]?.[0] || 0,
        totalCosts: routeCost.totalCost || 0,
      },
      responsibleId: profile?.userId,
      status: true,
      // Contract: linkedVehicles is always a single item (drivers[] when multi).
      linkedVehicles:
        navigateState?.edit && driversSelected.length > 0 ? [linkedVehicleItem] : [],
      responseHere: { routes },
      duration: durationWithStops,
      distance: distance,
      dnGroupId: getDnGroupIdFromUrl(),
      dnGroupName: getDnGroupNameFromUrl(),
    };

    params.set("custom", isCustom?.toString());
    dispatch(setIsCustomTrip(isCustom));
    dispatch(setRouteTripSelected(body as any));

    if (navigateState?.edit) {
      await submitTripUpdate({ ...body, smartRoutePreference, fuelOption });
    } else {
      await submitTripSmartRoute({ ...body, smartRoutePreference, fuelOption });
    }
  };

  const handleEditDriverTrip = () => {
    params.set("custom", isCustom?.toString());
    dispatch(setIsCustomTrip(isCustom));
    navigate("/routing/trips/driver?" + params.toString(), { state: { edit: navigateState?.edit } });
  };

  const handleEditVehicleTrip = () => {
    params.set("custom", isCustom?.toString());
    dispatch(setIsCustomTrip(isCustom));
    navigate("/routing/trips/vehicle?" + params.toString(), { state: { edit: navigateState?.edit } });
  };
  const hasFetchedAreas = useRef(false);

  const fetchAndApplyLinkedAreas = async (routeId: string) => {
    if (hasFetchedAreas.current) return;

    try {
      const firstPageResponse: ReworkAreasResponse = await getAllAreas("", 1, 10000, "name,asc", routeId);
      let allAreas: CreateAreaReworkPayload[] = firstPageResponse.items || [];
      const fetchPagePromises: Promise<ReworkAreasResponse>[] = [];
      if (fetchPagePromises.length > 0) {
        const otherPagesResponses = await Promise.all(fetchPagePromises);
        otherPagesResponses.forEach((pageResp, idx) => {
          allAreas = allAreas.concat(pageResp.items || []);
        });
      }

      const avoidAreas = allAreas.filter((a) => a.interestType === "AVOID" && a.selected === true);
      const interestAreas = allAreas.filter((a) => a.interestType === "INTEREST" && a.selected === true);
      const merged = [...avoidAreas, ...interestAreas]
      dispatch(defineAreasRework(merged));
      dispatch(defineCopyAreasRework(merged));
      
      const formatted = avoidAreas
        .filter((area) => area.selected === true)
        .map((area) => {
          const isCircle = area.circleRadius !== null && area.circleRadius !== undefined;
          if (isCircle) {
            const center = area.points[0];
            const radius = area.circleRadius!;
            const offset = calculateOffsetCoordinates(center.lat, center.lng, radius);
            return `corridor:${center.lat},${center.lng};${offset.lat},${offset.lng};r=${Math.round(radius)}`;
          }
          return formatPolygon(area.points);
        })
        .join("|");

      dispatch(defineAvoidAreas({ formatted }));

      hasFetchedAreas.current = true;
    } catch (err) {
      console.error("Erro ao buscar áreas vinculadas à rota:", err);
    }
  };
 

  useEffect(() => {
    if (routeId) {
      fetchAndApplyLinkedAreas(routeId)
    }
  }, [routeId])

  return (
    <div className="width-100pct height-100pct">
      <MapDrawingProvider>
        <UpdateSidebarRoute
          skipChangeRoute={skip}
          handleRoutes={handleRoutes}
          sendRoute={(data) => {
            dispatch(setTripUpdateClean());
            sendRoute(data);
          }}
          trip={!!params.get("trip")}
          isCustom={isCustom}
          handleEditDriverTrip={handleEditDriverTrip}
          handleEditVehicleTrip={handleEditVehicleTrip}
          isEditTrip={navigateState?.edit}
          handleCustom={(value) => {
            setIsCustom(value);
          }}
          content={content}
          onContent={onContent}
        />

        <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct">
          <RioMap
            position={position}
            boundingBox={boundingBox}
            apiKey={process.env.REACT_APP_RIO_MAP_KEY as string}
            segments={segments.length ? [{ points: segments, alternative: false }] : []}
            markers={markers}
            useClusters={true}
          />
        </ApplicationLayout.Body>
      </MapDrawingProvider>
    </div>
  );
};

export default RoutesTripUpdate;

